import { useCallback, useEffect, useRef, useState } from "react";
import { COLORS } from "../../lib/brand";
import {
  Alert,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import QRCode from "react-native-qrcode-svg";
import Svg, { Path } from "react-native-svg";

const CORAVU_LOGO = require("../../assets/icon.png");

import { Button } from "../ui";
import { TeamIconButton } from "./TeamControlIcons";
import {
  buildInviteUrl,
  createTeamInvite,
  listTeamInvites,
  revokeTeamInvite,
  type TeamInviteRow,
} from "../../lib/teamInvites";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.45)";

const EXPIRY_OPTIONS: { label: string; days: number | null }[] = [
  { label: "24 hours", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "Never", days: null },
];

const USES_OPTIONS: { label: string; value: number | null }[] = [
  { label: "Single use", value: 1 },
  { label: "Up to 5", value: 5 },
  { label: "Up to 25", value: 25 },
  { label: "Unlimited", value: null },
];

export function TeamInviteSection({
  teamId,
  isCaptain,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: {
  teamId: string;
  isCaptain: boolean;
  /** When provided, the component is controlled — the parent owns the open
   *  state and the panel responds to this prop instead of internal state. */
  open?: boolean;
  /** Called whenever the panel wants to open/close. Pair with `open`. */
  onOpenChange?: (next: boolean) => void;
  /** Suppress the default TeamIconButton trigger. Use when the parent renders
   *  its own button (e.g. the "+ Add member" affordance under the roster). */
  hideTrigger?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    if (controlledOpen === undefined) setInternalOpen(next);
  };
  const [invites, setInvites] = useState<TeamInviteRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiryDays, setExpiryDays] = useState<number | null>(7);
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [showQrFor, setShowQrFor] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!isCaptain) return;
    setLoading(true);
    const rows = await listTeamInvites(teamId);
    setInvites(rows);
    setLoading(false);
  }, [teamId, isCaptain]);

  useEffect(() => {
    if (open) void reload();
  }, [open, reload]);

  if (!isCaptain) return null;

  async function onCreate() {
    setCreating(true);
    const expiresAt = expiryDays == null ? null : new Date(Date.now() + expiryDays * 86_400_000);
    const { invite, error } = await createTeamInvite({
      teamId,
      expiresAt,
      maxUses,
    });
    setCreating(false);
    if (error) {
      Alert.alert("Could not create invite", error.message);
      return;
    }
    if (invite) setShowQrFor(invite.slug);
    await reload();
  }

  async function onRevoke(inviteId: string) {
    // Optimistically drop the row so the captain sees it disappear immediately;
    // we'll let `reload()` reconcile if the server returns a different shape.
    setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    // Also clear any QR popup pinned to that invite.
    setShowQrFor((prevSlug) => {
      const target = invites.find((i) => i.id === inviteId);
      return target && prevSlug === target.slug ? null : prevSlug;
    });
    const { error } = await revokeTeamInvite(inviteId);
    if (error) {
      Alert.alert("Could not revoke", error.message);
      // Roll back the optimistic removal.
      await reload();
      return;
    }
    await reload();
  }

  // Hide revoked invites entirely — the captain wants them gone from the list,
  // not greyed out. Also drop expired/exhausted ones so the panel stays focused
  // on what's still actionable.
  const visibleInvites = invites.filter((i) => {
    if (i.revoked_at != null) return false;
    if (i.expires_at != null && new Date(i.expires_at).getTime() < Date.now()) return false;
    if (i.max_uses != null && i.uses_count >= i.max_uses) return false;
    return true;
  });

  const activeShownInvite = showQrFor
    ? invites.find((i) => i.slug === showQrFor) ?? null
    : null;

  return (
    <>
      {hideTrigger ? null : (
        <TeamIconButton
          label="Invite link"
          description="Create a one-tap join link or QR code. Send it to friends or scan it in person — recipients auto-register for the case and join the team without going through the request flow."
          onPress={() => setOpen(true)}
          icon={(c) => <LinkGlyph color={c} />}
        />
      )}

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => {
            setOpen(false);
            setShowQrFor(null);
          }}
          style={[
            {
              flex: 1,
              // Stronger dim + blur creates a real barrier between the modal
              // and the page behind it.
              backgroundColor: "rgba(13,20,12,0.62)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 16,
              paddingVertical: 32,
            },
            Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                } as unknown as ViewStyle)
              : null,
          ]}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            // The panel is height-constrained so the inner ScrollView handles
            // overflow cleanly when the active-invites list grows. Stronger
            // border + shadow lifts the card off the blurred page.
            style={[
              {
                width: "100%",
                maxWidth: 820,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: "rgba(28,43,26,0.18)",
                backgroundColor: COLORS.WHITE.hex,
                overflow: "hidden",
                maxHeight: "92%" as unknown as number,
              },
              Platform.OS === "web"
                ? ({
                    boxShadow:
                      "0 1px 0 rgba(255,255,255,0.6) inset, 0 32px 80px -20px rgba(0,0,0,0.55)",
                  } as unknown as ViewStyle)
                : {},
            ]}
          >
            <ScrollView
              style={{ flexGrow: 0 }}
              contentContainerStyle={{ padding: 0 }}
              showsVerticalScrollIndicator
            >
              {/* Header — modest top padding that mirrors the footer below
                  so the panel reads as balanced top-to-bottom. */}
              <View className="px-9 pt-7 pb-9">
                <View className="flex-row items-center gap-3">
                  <View style={{ width: 28, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
                  <Text
                    className="text-[11px] font-bold uppercase tracking-widest-3"
                    style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 2 }}
                  >
                    Invite to team
                  </Text>
                </View>
                <Text
                  className="mt-5 text-[34px] leading-tight text-forest"
                  style={{ fontFamily: "CormorantGaramond_500Medium" }}
                >
                  Bring people in directly
                </Text>
                <Text
                  className="mt-4 text-[15px] leading-7"
                  style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
                >
                  Generate a link or QR code. Whoever opens it lands on a confirm page, then auto-registers for the case
                  and joins this team — no request flow needed.
                </Text>
              </View>

              {/* Inset divider — sits inside the panel margins instead of
                  running edge-to-edge so the panel reads as a tidy card. */}
              <SectionDivider />

              {/* Create new invite */}
              <View className="px-9 py-8">
                <Text
                  className="text-[12px] font-bold uppercase"
                  style={{ fontFamily: "DMSans_700Bold", color: TEXT_MUTED, letterSpacing: 1.6 }}
                >
                  New invite
                </Text>

                <View className="mt-5">
                  <Text
                    className="mb-3 text-[12px] uppercase"
                    style={{ fontFamily: "DMSans_500Medium", color: TEXT_LIGHT, letterSpacing: 1.4 }}
                  >
                    Expires in
                  </Text>
                  <View className="flex-row flex-wrap gap-2.5">
                    {EXPIRY_OPTIONS.map((opt) => {
                      const active = opt.days === expiryDays;
                      return (
                        <Chip
                          key={opt.label}
                          label={opt.label}
                          active={active}
                          onPress={() => setExpiryDays(opt.days)}
                        />
                      );
                    })}
                  </View>
                </View>

                <View className="mt-6">
                  <Text
                    className="mb-3 text-[12px] uppercase"
                    style={{ fontFamily: "DMSans_500Medium", color: TEXT_LIGHT, letterSpacing: 1.4 }}
                  >
                    Use limit
                  </Text>
                  <View className="flex-row flex-wrap gap-2.5">
                    {USES_OPTIONS.map((opt) => {
                      const active = opt.value === maxUses;
                      return (
                        <Chip
                          key={opt.label}
                          label={opt.label}
                          active={active}
                          onPress={() => setMaxUses(opt.value)}
                        />
                      );
                    })}
                  </View>
                </View>

                <View className="mt-8 mb-1 self-start">
                  <Button variant="primary" loading={creating} onPress={() => void onCreate()}>
                    Generate invite
                  </Button>
                </View>
              </View>

              {/* QR popup for the most recently created invite */}
              {activeShownInvite ? (
                <>
                  <SectionDivider />
                  <View
                    className="px-9 py-8"
                    style={{ backgroundColor: "rgba(181,135,58,0.04)" }}
                  >
                    <InviteCard
                      invite={activeShownInvite}
                      onCopy={() => copyInviteUrl(activeShownInvite.slug)}
                      onClose={() => setShowQrFor(null)}
                    />
                  </View>
                </>
              ) : null}

              <SectionDivider />

              {/* List of existing invites */}
              <View className="px-9 py-8">
                <Text
                  className="text-[12px] font-bold uppercase"
                  style={{ fontFamily: "DMSans_700Bold", color: TEXT_MUTED, letterSpacing: 1.6 }}
                >
                  Active invites
                </Text>
                {loading ? (
                  <Text
                    className="mt-4 text-[14px]"
                    style={{ fontFamily: "DMSans_400Regular", color: TEXT_LIGHT }}
                  >
                    Loading…
                  </Text>
                ) : visibleInvites.length === 0 ? (
                  <Text
                    className="mt-4 text-[14px]"
                    style={{ fontFamily: "DMSans_400Regular", color: TEXT_LIGHT }}
                  >
                    No active invites — generate one above.
                  </Text>
                ) : (
                  <View className="mt-4 gap-2.5">
                    {visibleInvites.map((inv) => (
                      <InviteRow
                        key={inv.id}
                        invite={inv}
                        onCopy={() => copyInviteUrl(inv.slug)}
                        onShowQr={() => setShowQrFor(inv.slug)}
                        onRevoke={() => void onRevoke(inv.id)}
                      />
                    ))}
                  </View>
                )}
              </View>

              <SectionDivider />

              {/* Footer — modest bottom padding so "Done" reads as anchored to
                  the bottom of the panel without feeling weighted down. */}
              <View
                className="flex-row items-center justify-end gap-3 px-9 pt-6 pb-6"
                style={{ backgroundColor: "rgba(28,43,26,0.025)" }}
              >
                <Button variant="link" onPress={() => setOpen(false)}>
                  Done
                </Button>
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

/**
 * Inset hairline used to separate sections inside the invite modal. Sits
 * 32px in from each side instead of running edge-to-edge so the panel keeps
 * its card silhouette.
 */
function SectionDivider() {
  return (
    <View
      style={{
        height: 1,
        marginHorizontal: 32,
        backgroundColor: "rgba(28,43,26,0.18)",
      }}
    />
  );
}

function copyInviteUrl(slug: string) {
  const url = buildInviteUrl(slug);
  if (Platform.OS === "web" && typeof navigator !== "undefined" && navigator.clipboard) {
    void navigator.clipboard.writeText(url);
  }
}

function Chip({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? "rgba(181,135,58,0.45)" : SAND_DARK,
          backgroundColor: active ? "rgba(181,135,58,0.10)" : "rgba(255,255,255,0.6)",
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        className="text-[12.5px] uppercase"
        style={{
          fontFamily: "DMSans_700Bold",
          letterSpacing: 1.4,
          color: active ? AMBER : TEXT_MUTED,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function InviteCard({
  invite,
  onCopy,
  onClose,
}: {
  invite: TeamInviteRow;
  onCopy: () => void;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = buildInviteUrl(invite.slug);
  // react-native-qrcode-svg exposes a `getRef` callback that hands back the
  // underlying SVG element. We stash it so the Download button can call
  // `.toDataURL((data) => …)` to get a base64 PNG and trigger a save.
  const qrRef = useRef<{ toDataURL: (cb: (data: string) => void) => void } | null>(null);

  function downloadQr() {
    if (!qrRef.current || typeof qrRef.current.toDataURL !== "function") {
      Alert.alert("Could not download", "QR rendering is not ready yet — try again in a moment.");
      return;
    }
    qrRef.current.toDataURL((data: string) => {
      if (Platform.OS !== "web" || typeof document === "undefined") {
        // Native: fall back to copying the link so the user has something
        // shareable. (A native save flow would require expo-file-system /
        // expo-media-library wiring that the team page hasn't pulled in yet.)
        Alert.alert(
          "Save QR",
          "Native QR-image saving isn't wired up yet. Use Copy link or open this team page on web to download the PNG."
        );
        return;
      }

      // The QR component's `toDataURL` only encodes the modules — the on-screen
      // Coravu logo is overlaid as a separate React node and isn't part of that
      // PNG. To match what the user sees, we render the QR onto a canvas, then
      // paint a white square + the Coravu mark on top before exporting.
      const baseSize = 184;
      const exportScale = 4; // Up-res so the saved PNG looks sharp.
      const canvas = document.createElement("canvas");
      canvas.width = baseSize * exportScale;
      canvas.height = baseSize * exportScale;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dataUrl = `data:image/png;base64,${data}`;
      const qrImg = new window.Image();
      qrImg.crossOrigin = "anonymous";
      qrImg.onload = () => {
        ctx.fillStyle = COLORS.WHITE.hex;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(qrImg, 0, 0, canvas.width, canvas.height);

        // Draw the white logo plate + the Coravu mark centered.
        const logoSize = 40 * exportScale;
        const margin = 4 * exportScale;
        const x = (canvas.width - logoSize) / 2;
        const y = (canvas.height - logoSize) / 2;
        ctx.fillStyle = COLORS.WHITE.hex;
        ctx.fillRect(x - margin, y - margin, logoSize + margin * 2, logoSize + margin * 2);

        // Resolve the logo asset to a URL. Different bundler / platform
        // pairings hand `require(...)` back as a string, an object with a
        // `uri`, or an asset id that needs `Image.resolveAssetSource`.
        // Try each shape in order so the export keeps working on web even
        // when `resolveAssetSource` isn't part of the Expo web runtime.
        const resolveLogoUri = (): string => {
          const raw = CORAVU_LOGO as unknown;
          if (typeof raw === "string") return raw;
          if (raw && typeof raw === "object") {
            const obj = raw as { uri?: string; default?: string | { uri?: string } };
            if (typeof obj.uri === "string") return obj.uri;
            if (typeof obj.default === "string") return obj.default;
            if (obj.default && typeof obj.default === "object" && obj.default.uri) {
              return obj.default.uri;
            }
          }
          const resolver = (Image as unknown as {
            resolveAssetSource?: (s: unknown) => { uri?: string } | null;
          }).resolveAssetSource;
          if (typeof resolver === "function") {
            try {
              return resolver(CORAVU_LOGO)?.uri ?? "";
            } catch {
              return "";
            }
          }
          return "";
        };
        const logoUri = resolveLogoUri();
        const logoImg = new window.Image();
        logoImg.crossOrigin = "anonymous";
        const finish = () => {
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/png");
          link.download = `coravu-invite-${invite.slug}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        logoImg.onload = () => {
          ctx.drawImage(logoImg, x, y, logoSize, logoSize);
          finish();
        };
        logoImg.onerror = () => {
          // Logo couldn't be loaded — still let the user save the QR with just
          // the white plate so the export isn't blocked.
          finish();
        };
        if (logoUri) {
          logoImg.src = logoUri;
        } else {
          finish();
        }
      };
      qrImg.onerror = () => {
        Alert.alert("Could not download", "Something went wrong while preparing the image.");
      };
      qrImg.src = dataUrl;
    });
  }

  return (
    <View>
      <Text
        className="text-[12px] font-bold uppercase"
        style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.6 }}
      >
        Your invite is ready
      </Text>
      <View className="mt-4 flex-row items-start gap-5">
        <View
          style={{
            backgroundColor: COLORS.WHITE.hex,
            padding: 10,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: SAND_DARK,
          }}
        >
          <QRCode
            value={url}
            size={184}
            backgroundColor={COLORS.WHITE.hex}
            color={FOREST}
            // Embed the Coravu mark in the center of the QR code. Stays small
            // enough that the QR code's error-correction can recover the
            // covered modules — roughly 20% of the QR size with a white
            // padding ring around it so the scanner reads it cleanly.
            logo={CORAVU_LOGO}
            logoSize={40}
            logoMargin={4}
            logoBackgroundColor={COLORS.WHITE.hex}
            logoBorderRadius={6}
            ecl="H"
            getRef={(c) => {
              qrRef.current = c as unknown as {
                toDataURL: (cb: (data: string) => void) => void;
              } | null;
            }}
          />
        </View>
        <View className="min-w-0 flex-1">
          <Text
            className="text-[14px] leading-[20px]"
            style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
          >
            Share the QR for in-person pickups, or the link for DMs and email.
          </Text>
          <View
            className="mt-3 rounded-md border px-3 py-2.5"
            style={{ borderColor: SAND_DARK, backgroundColor: COLORS.WHITE.hex }}
          >
            <Text
              className="text-[13px]"
              style={{ fontFamily: "DMSans_500Medium", color: FOREST }}
              numberOfLines={2}
            >
              {url}
            </Text>
          </View>
          <View className="mt-4 flex-row flex-wrap gap-2.5">
            <Button
              variant="primary"
              onPress={() => {
                onCopy();
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              }}
            >
              {copied ? "Copied ✓" : "Copy link"}
            </Button>
            <Button variant="ghost-light" onPress={downloadQr}>
              Download QR
            </Button>
            <Button variant="link" onPress={onClose}>
              Close
            </Button>
          </View>
        </View>
      </View>
    </View>
  );
}

function InviteRow({
  invite,
  onCopy,
  onShowQr,
  onRevoke,
}: {
  invite: TeamInviteRow;
  onCopy: () => void;
  onShowQr: () => void;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const expired = invite.expires_at != null && new Date(invite.expires_at).getTime() < Date.now();
  const exhausted = invite.max_uses != null && invite.uses_count >= invite.max_uses;
  const revoked = invite.revoked_at != null;
  const inactive = expired || exhausted || revoked;
  const status = revoked
    ? "Revoked"
    : expired
      ? "Expired"
      : exhausted
        ? "Used up"
        : invite.expires_at
          ? `Expires ${new Date(invite.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
          : "Active";
  const usesLabel =
    invite.max_uses == null
      ? `${invite.uses_count} uses`
      : `${invite.uses_count} / ${invite.max_uses} uses`;

  return (
    <View
      className="rounded-md border px-5 py-4"
      style={{
        borderColor: SAND_DARK,
        backgroundColor: inactive ? "rgba(28,43,26,0.025)" : COLORS.WHITE.hex,
        opacity: inactive ? 0.75 : 1,
      }}
    >
      <View className="flex-row items-baseline justify-between gap-3">
        <Text
          className="min-w-0 flex-1 text-[14px]"
          style={{ fontFamily: "DMSans_600SemiBold", color: FOREST }}
          numberOfLines={1}
        >
          {invite.slug}
        </Text>
        <Text
          className="text-[11px] uppercase"
          style={{
            fontFamily: "DMSans_700Bold",
            color: revoked || expired ? "#C0392B" : exhausted ? AMBER : TEXT_LIGHT,
            letterSpacing: 1.4,
          }}
        >
          {status}
        </Text>
      </View>
      <Text
        className="mt-1.5 text-[12px]"
        style={{ fontFamily: "DMSans_400Regular", color: TEXT_LIGHT }}
      >
        {usesLabel}
      </Text>
      <View className="mt-4 flex-row flex-wrap gap-4">
        {!inactive ? (
          <>
            <Pressable
              onPress={() => {
                onCopy();
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              }}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
            >
              <Text
                className="text-[12px] font-bold uppercase"
                style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.6 }}
              >
                {copied ? "Copied" : "Copy link"}
              </Text>
            </Pressable>
            <Pressable
              onPress={onShowQr}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
            >
              <Text
                className="text-[12px] font-bold uppercase"
                style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.6 }}
              >
                Show QR
              </Text>
            </Pressable>
            <Pressable
              onPress={onRevoke}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
            >
              <Text
                className="text-[12px] font-bold uppercase"
                style={{ fontFamily: "DMSans_700Bold", color: TEXT_MUTED, letterSpacing: 1.6 }}
              >
                Revoke
              </Text>
            </Pressable>
          </>
        ) : null}
      </View>
    </View>
  );
}

/**
 * Chain-link glyph — the standard "shareable link" icon. Two interlocked
 * rounded segments at a slight diagonal. Stroke and viewBox are tuned so the
 * glyph fills its container at default size 16, matching the visual weight
 * of Padlock / EyeIcon / LeaveIcon / TrashIcon when rendered inside a 34×34
 * TeamIconButton.
 */
function LinkGlyph({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M10.5 13.5a4.5 4.5 0 0 1 0-6.4l2.6-2.6a4.5 4.5 0 0 1 6.4 6.4l-1.7 1.7"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Path
        d="M13.5 10.5a4.5 4.5 0 0 1 0 6.4l-2.6 2.6a4.5 4.5 0 0 1-6.4-6.4l1.7-1.7"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </Svg>
  );
}
